/**
 * 创建静态签名formatter的工厂函数
 *
 * 【背景】
 * hiprint的html控件在不同阶段（设计、预览、打印、导出PDF）会多次调用formatter函数。
 * 原始实现依赖options.variables参数动态生成HTML，但在渲染阶段该参数会丢失，
 * 导致"options.variables is not defined"错误。
 *
 * 【问题原因】
 * 1. hiprint在序列化/反序列化模板时，自定义的options.variables属性会丢失
 * 2. 使用闭包的formatter函数在某些情况下会失去对外部变量的引用
 * 3. 设计阶段和渲染阶段的数据传递机制不一致
 *
 * 【解决方案】
 * 1. 在控件创建时就将variables转换为静态HTML模板
 * 2. 使用new Function()动态生成formatter函数，将HTML硬编码到函数体中
 * 3. 避免依赖闭包和外部变量，确保formatter函数完全自包含
 * 4. 根据data参数的存在情况采用不同的渲染策略
 *
 * @param {Array} variables - 业务字段变量数组，包含name和field属性
 * @returns {Function} 返回一个完全静态的formatter函数
 */
function createStaticSignatureFormatter(variables) {
  // 根据variables生成静态HTML内容
  var staticContent = ''

  if (variables && variables.length > 0) {
    staticContent = variables
      .map(function (item) {
        return (
          '<div style="margin-bottom: 2pt; display: flex; align-items: flex-start;">' +
          '<label style="font-weight: bold; min-width: 80pt; display: inline-block; color: #333333;">' +
          item.name +
          '：</label>' +
          '<span style="flex: 1; word-break: break-word;">${' +
          item.field +
          '}</span>' +
          '</div>'
        )
      })
      .join('')
  }

  // 生成完整的HTML模板
  var templateHTML =
    "<div style=\"padding: 8pt; font-family: 'SimSun', '宋体', serif; font-size: 12pt; line-height: 1.6; color: #000000; box-sizing: border-box;\">" +
    (staticContent || '<span style="color: #999999;">配置为空，请联系管理员</span>') +
    '</div>'

  /**
   * 动态生成formatter函数代码
   * 将HTML模板硬编码到函数体中，避免闭包问题
   *
   * 渲染策略：
   * 1. 有数据时：解析${field}占位符，支持嵌套属性（如obj.prop）
   * 2. 无数据时：直接清除所有${...}占位符，显示纯标签
   */
  var formatterCode =
    'return function (t, options, data) {\n' +
    '  var html = ' +
    JSON.stringify(templateHTML) +
    ';\n' +
    '  \n' +
    '  if (data && Object.keys(data).length > 0) {\n' +
    '    // 有数据：智能替换变量占位符\n' +
    '    html = html.replace(/\\${(\\S+)}/g, function (match, key) {\n' +
    '      try {\n' +
    '        var keys = key.split(".");\n' +
    '        var value = data;\n' +
    '        \n' +
    '        // 支持嵌套属性访问（如：user.profile.name）\n' +
    '        for (var i = 0; i < keys.length; i++) {\n' +
    '          if (value && value.hasOwnProperty(keys[i])) {\n' +
    '            value = value[keys[i]];\n' +
    '          } else {\n' +
    '            value = "";\n' +
    '            break;\n' +
    '          }\n' +
    '        }\n' +
    '        return value || "";\n' +
    '      } catch (error) {\n' +
    '        return "";\n' +
    '      }\n' +
    '    });\n' +
    '  } else {\n' +
    '    // 无数据：清除占位符，显示纯标签结构\n' +
    '    html = html.replace(/\\${[^}]+}/g, "");\n' +
    '  }\n' +
    '  \n' +
    '  return html;\n' +
    '};'

  // 使用new Function创建完全独立的formatter函数
  return new Function(formatterCode)()
}

export default function (hiprint) {
  return function (options) {
    var addElementTypes = function (context) {
      context.removePrintElementTypes('defaultModule')
      context.addPrintElementTypes('defaultModule', [
        new hiprint.PrintElementTypeGroup('基础控件', [
          {
            tid: 'defaultModule.text',
            title: '文本',
            data: '',
            type: 'text',
          },
          {
            tid: 'defaultModule.image',
            title: '图片',
            data: '',
            type: 'image',
          },
          {
            tid: 'defaultModule.longText',
            title: '长文',
            data: '155123456789',
            type: 'longText',
          },
          {
            tid: 'defaultModule.emptyTable',
            title: '空白表格',
            type: 'table',
            field: 'tableData',
            border: true,
            editable: true,
            columnDisplayEditable: true, //列显示是否能编辑
            columnDisplayIndexEditable: true, //列顺序显示是否能编辑
            columnTitleEditable: true, //列标题是否能编辑
            columnResizable: true, //列宽是否能调整
            columnAlignEditable: true, //列对齐是否调整
            isEnableEditField: true, //编辑字段
            isEnableContextMenu: true, //开启右键菜单 默认true
            isEnableInsertRow: false, //插入行
            isEnableDeleteRow: false, //删除行
            isEnableInsertColumn: false, //插入列
            isEnableDeleteColumn: true, //删除列
            isEnableMergeCell: false, //合并单元格
            columns: [
              [
                {
                  title: '序号',
                  field: 'id',
                  width: '60',
                  align: 'center',
                },
              ],
            ],
          },
          {
            tid: 'defaultModule.html',
            title: 'html',
            formatter: function (data, options) {
              return `
                <div class="example-item">
                  <div style="padding: 10pt; background: #f0f9ff; border-radius: 4pt;">
                    <i class="iconfont icon-html" style="color: #409eff; margin-right: 5pt;"></i>
                    <span style="color: #409eff;">信息文本</span>
                  </div>
                </div>
              `
            },
            type: 'html',
          },
          {
            tid: 'defaultModule.hline',
            title: '横线',
            type: 'hline',
          },
          {
            tid: 'defaultModule.vline',
            title: '竖线',
            type: 'vline',
          },
          {
            tid: 'defaultModule.rect',
            title: '矩形',
            type: 'rect',
          },
          {
            tid: 'defaultModule.oval',
            title: '椭圆',
            type: 'oval',
          },
          {
            tid: 'defaultModule.barcode',
            title: '条形码',
            type: 'barcode',
          },
          {
            tid: 'defaultModule.qrcode',
            title: '二维码',
            type: 'qrcode',
          },
          {
            tid: 'defaultModule.customText',
            title: '自定义文本',
            customText: '自定义文本',
            custom: true,
            type: 'text',
          },
        ]),
        new hiprint.PrintElementTypeGroup('自定义', createKeyValueType(options)),
      ])
    }
    return {
      addElementTypes: addElementTypes,
    }
  }
}
// 创建自定义类型控件
function createKeyValueType(options) {
  // 判断options是否为数组 不是数组则格式化为数组
  if (!Array.isArray(options)) {
    options = [options]
  }
  const res = options
    .filter((item) => item.fieldType !== 'tablecolumn')
    .map((item) => getTypeTemplate(item))
  return res
}

function getTypeTemplate(item) {
  switch (item.fieldType) {
    case 'normal':
      return {
        tid: `customModule.${item.field}.${item.fieldType}`,
        title: '',
        // data: item.name || '',
        type: 'text',
        options: {
          field: item.field,
          testData: item.defaultValue || '',
        },
      }
    case 'longtext':
      return {
        tid: `customModule.${item.field}.${item.fieldType}`,
        title: '',
        // data: item.name || '',
        type: 'longText',
        options: {
          field: item.field,
          testData: item.defaultValue || '',
        },
      }
    case 'combination':
      return {
        tid: `customModule.${item.field}.${item.fieldType}`,
        title: item.name,
        data: item.defaultValue || '',
        type: 'text',
        options: {
          field: item.field,
          testData: item.defaultValue || '',
          height: 16,
          fontSize: 6.75,
          fontWeight: '700',
          textAlign: 'left',
          textContentVerticalAlign: 'middle',
        },
      }
    case 'a-signature':
    case 'b-signature':
      var staticFormatter = createStaticSignatureFormatter(item.variables)
      return {
        tid: `customModule.${item.field}.${item.fieldType}`,
        title: item.name,
        type: 'html',
        options: {
          field: item.field,
          width: 280,
          height: 200,
        },
        formatter: staticFormatter,
      }
    default:
      return null
  }
}
